import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { 
  Plus, 
  Minus, 
  Trash2, 
  ShoppingCart, 
  ArrowLeft
} from 'lucide-react';
import PriceDisplay from '../components/PriceDisplay';
import { getImageUrl } from '../utils/api';
import { MetaPixelService } from '../services/metaPixel';

const Cart: React.FC = () => {
  const { items, total, itemCount, updateCartItem, removeFromCart, isLoading } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [updatingItems, setUpdatingItems] = useState<Set<string>>(new Set());

  // Track cart page view
  useEffect(() => {
    if (items.length > 0) {
      MetaPixelService.trackCustomEvent('ViewCart', {
        content_ids: items.map(item => item.product._id),
        contents: items.map(item => ({
          id: item.product._id,
          quantity: item.quantity
        })),
        value: total,
        currency: 'INR',
        num_items: itemCount
      });
    }
  }, [items, total, itemCount]);

  const handleQuantityUpdate = async (itemId: string, newQuantity: number) => {
    if (newQuantity < 1) return;
    
    setUpdatingItems(prev => new Set(prev).add(itemId));
    try {
      await updateCartItem(itemId, newQuantity);
    } catch (error) {
      console.error('Error updating cart item:', error);
    } finally {
      setUpdatingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  const handleRemoveItem = async (itemId: string) => {
    console.log('Attempting to remove item:', itemId);
    
    // Find the item being removed for tracking
    const itemToRemove = items.find(item => item.product._id === itemId);
    
    setUpdatingItems(prev => new Set(prev).add(itemId));
    try {
      await removeFromCart(itemId);
      console.log('Item removed successfully');
      
      // Track item removal
      if (itemToRemove) {
        MetaPixelService.trackCustomEvent('RemoveFromCart', {
          content_ids: [itemToRemove.product._id],
          content_type: 'product',
          value: itemToRemove.product.price * itemToRemove.quantity,
          currency: 'INR'
        });
      }
    } catch (error) {
      console.error('Error removing cart item:', error);
      alert('Failed to remove item from cart. Please try again.');
    } finally {
      setUpdatingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white dark:bg-secondary-900 transition-colors">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading cart...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-white dark:bg-secondary-900 transition-colors">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center py-12">
            <ShoppingCart className="h-16 w-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Please log in to view your cart
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              You need to be logged in to access your shopping cart.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/login"
                className="bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 
                         text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Log In
              </Link>
              <Link
                to="/register"
                className="border border-primary-600 dark:border-primary-500 text-primary-600 dark:text-primary-400 
                         hover:bg-primary-50 dark:hover:bg-primary-900/20 px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Sign Up
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-white dark:bg-secondary-900 transition-colors">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
          {/* Header */}
          <div className="flex items-center mb-6 sm:mb-8">
            <button
              onClick={() => navigate(-1)}
              className="mr-4 p-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 
                       rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-800 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
              Shopping Cart
            </h1>
            <span className="ml-2 text-lg text-gray-500 dark:text-gray-400">
              (0 items)
            </span>
          </div>

          {/* Empty Cart */}
          <div className="text-center py-12">
            <ShoppingCart className="h-16 w-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Your cart is empty
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
              Looks like you haven't added any items to your cart yet. Start shopping to fill it up!
            </p>
            <Link
              to="/products"
              className="inline-flex items-center bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 
                       text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              <ShoppingCart className="h-5 w-5 mr-2" />
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-secondary-900 transition-colors pb-20 sm:pb-8">
      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
        {/* Header */}
        <div className="flex items-center mb-6 sm:mb-8">
          <button
            onClick={() => navigate(-1)}
            className="mr-3 sm:mr-4 p-2.5 sm:p-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 
                     rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-800 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
              Shopping Cart
            </h1>
            <span className="text-sm sm:text-base lg:text-lg text-gray-500 dark:text-gray-400">
              {itemCount} {itemCount === 1 ? 'item' : 'items'}
            </span>
          </div>
        </div>

        <div className="lg:grid lg:grid-cols-12 lg:gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-8">
            <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-gray-200 dark:border-secondary-700">
              <div className="divide-y divide-gray-200 dark:divide-secondary-700">
                {items.map((item) => {
                  const isUpdating = updatingItems.has(item._id);
                  console.log('Cart item:', item); // Debug log
                  
                  return (
                    <div key={item._id} className="p-4 sm:p-6">
                      <div className="flex flex-col sm:flex-row sm:items-start space-y-4 sm:space-y-0 sm:space-x-4">
                        {/* Product Image */}
                        <div className="flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 bg-gray-200 dark:bg-secondary-700 rounded-xl overflow-hidden">
                          <img
                            src={getImageUrl(item.product.images[0])}
                            alt={item.product.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>

                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:justify-between">
                            <div className="flex-1 pr-0 sm:pr-4">
                              <Link
                                to={`/products/${item.product._id}`}
                                className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100 
                                         hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                              >
                                {item.product.name}
                              </Link>
                              <div className="mt-1 sm:mt-2 space-y-1">
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  Category: {item.product.category}
                                </p>
                                {item.product.brand && (
                                  <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Brand: {item.product.brand}
                                  </p>
                                )}
                                {(item.size || item.color) && (
                                  <div className="flex flex-wrap gap-2">
                                    {item.size && (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs 
                                                     bg-gray-100 dark:bg-secondary-700 text-gray-800 dark:text-gray-200">
                                        Size: {item.size}
                                      </span>
                                    )}
                                    {item.color && (
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs 
                                                     bg-gray-100 dark:bg-secondary-700 text-gray-800 dark:text-gray-200">
                                        Color: {item.color}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Price */}
                            <div className="mt-4 sm:mt-0 text-right">
                              <p className="text-xl sm:text-2xl font-bold text-primary-600 dark:text-primary-400">
                                ₹{((item.product.discountedPrice || item.product.price) * item.quantity).toLocaleString()}
                              </p>
                              <PriceDisplay
                                price={item.product.price}
                                originalPrice={item.product.originalPrice}
                                discountedPrice={item.product.discountedPrice}
                                className="text-sm justify-end"
                                showDiscountPercentage={false}
                              />
                              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">per item</p>
                            </div>
                          </div>

                          {/* Quantity Controls and Actions */}
                          <div className="mt-4 flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
                            {/* Quantity Controls */}
                            <div className="flex items-center justify-between sm:justify-start sm:space-x-3">
                              <div className="flex items-center bg-gray-100 dark:bg-secondary-700 rounded-xl p-1">
                                <button
                                  onClick={() => handleQuantityUpdate(item._id, item.quantity - 1)}
                                  disabled={item.quantity <= 1 || isUpdating}
                                  className="p-3 sm:p-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 
                                           bg-white dark:bg-secondary-600 rounded-lg shadow-sm hover:bg-gray-50 dark:hover:bg-secondary-500
                                           disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px] min-w-[44px] 
                                           sm:min-h-[32px] sm:min-w-[32px] flex items-center justify-center"
                                >
                                  <Minus className="h-4 w-4" />
                                </button>
                                <span className="px-4 py-3 sm:py-2 text-gray-900 dark:text-gray-100 font-semibold min-w-[3rem] text-center text-lg sm:text-base">
                                  {isUpdating ? '...' : item.quantity}
                                </span>
                                <button
                                  onClick={() => handleQuantityUpdate(item._id, item.quantity + 1)}
                                  disabled={isUpdating}
                                  className="p-3 sm:p-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 
                                           bg-white dark:bg-secondary-600 rounded-lg shadow-sm hover:bg-gray-50 dark:hover:bg-secondary-500
                                           disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px] min-w-[44px] 
                                           sm:min-h-[32px] sm:min-w-[32px] flex items-center justify-center"
                                >
                                  <Plus className="h-4 w-4" />
                                </button>
                              </div>
                              <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 sm:ml-3">
                                {item.product.stock > 0 ? `${item.product.stock} in stock` : 'Out of stock'}
                              </span>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center justify-end sm:justify-start">
                              <button
                                onClick={() => handleRemoveItem(item._id)}
                                disabled={isUpdating}
                                className="text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 
                                         p-3 sm:p-2 rounded-xl sm:rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 
                                         disabled:opacity-50 disabled:cursor-not-allowed transition-colors
                                         min-h-[44px] min-w-[44px] sm:min-h-[36px] sm:min-w-[36px] flex items-center justify-center"
                                title="Remove from Cart"
                              >
                                <Trash2 className="h-5 w-5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Continue Shopping */}
            <div className="mt-6">
              <Link
                to="/products"
                className="inline-flex items-center text-primary-600 dark:text-primary-400 
                         hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Continue Shopping
              </Link>
            </div>
          </div>

          {/* Order Summary - Hidden on mobile, shown in sticky footer */}
          <div className="hidden lg:block lg:col-span-4">
            <div className="bg-white dark:bg-secondary-800 rounded-xl shadow-sm border border-gray-200 dark:border-secondary-700 p-6 sticky top-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Order Summary
              </h2>
              
              <div className="space-y-3">
                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                  <span>Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'items'})</span>
                  <span>₹{(total || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                  <span>Shipping</span>
                  <span>{(total || 0) >= 999 ? 'Free' : '₹50'}</span>
                </div>
                <div className="border-t border-gray-200 dark:border-secondary-600 pt-3">
                  <div className="flex justify-between text-lg font-semibold text-gray-900 dark:text-gray-100">
                    <span>Total</span>
                    <span>₹{((total || 0) + ((total || 0) >= 999 ? 0 : 50)).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {(total || 0) < 999 && (
                <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Add ₹{(999 - (total || 0)).toLocaleString()} more to get free shipping!
                  </p>
                </div>
              )}

              <button
                onClick={() => navigate('/checkout')}
                className="w-full mt-6 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 
                         text-white py-3 px-6 rounded-xl font-semibold transition-colors min-h-[48px]"
              >
                Proceed to Checkout
              </button>

              <div className="mt-4 text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Secure checkout with SSL encryption
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sticky Checkout Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-secondary-800 border-t border-gray-200 dark:border-secondary-700 p-4 lg:hidden z-40 shadow-xl">
        <div className="flex items-center justify-between space-x-4">
          <div className="flex-1">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {itemCount} {itemCount === 1 ? 'item' : 'items'}
            </div>
            <div className="text-xl font-bold text-gray-900 dark:text-gray-100">
              ₹{((total || 0) + ((total || 0) >= 999 ? 0 : 50)).toLocaleString()}
            </div>
            {(total || 0) < 999 && (
              <div className="text-xs text-blue-600 dark:text-blue-400">
                Add ₹{(999 - (total || 0)).toLocaleString()} for free shipping
              </div>
            )}
          </div>
          
          <button
            onClick={() => navigate('/checkout')}
            className="bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 
                     text-white px-6 py-4 rounded-xl font-semibold transition-all active:transform active:scale-[0.98] 
                     min-h-[56px] shadow-lg flex-shrink-0"
          >
            Proceed to Checkout
          </button>
        </div>
        
        {/* Safe area for iOS */}
        <div className="h-0 pb-safe"></div>
      </div>
    </div>
  );
};

export default Cart;